/**
 * lib/store/hydrate-masters.ts — Stage FW-2a
 *
 * Backend hydration for Stations + Hotels master data.
 * Called on app load (after auth) to replace mock JSON seed with live backend data.
 *
 * Crew stays on mock for now (PII handling unresolved).
 * Other seed data (flights, trainings, etc.) also stays on mock for now.
 */

import { fetchStations, fetchHotels, fetchRanks, fetchSystemConfig, fetchBranding } from '@/lib/api/masters';
import { useAppStore } from './index';

/**
 * Hydrate stations + hotels from backend, replacing the mock seed.
 * Other seed data (crew, flights, etc.) stays unchanged.
 *
 * This is called by the StoreHydration component after auth is ready.
 * Loading/error handling is done at the component level using
 * components/ui/loading-skeleton.tsx and error-state.tsx.
 */
export async function hydrateMastersFromBackend(): Promise<void> {
  // Fetch in parallel
  const [stations, hotels, ranks, systemConfig] = await Promise.all([
    fetchStations(),
    fetchHotels(),
    fetchRanks(),
    fetchSystemConfig(),
  ]);

  // Write to store. systemConfig comes only from the backend: null (no
  // config row for this tenant) leaves the features that need it off.
  useAppStore.setState({
    stations,
    hotels,
    ranks,
    tenantSettings: systemConfig?.settings ?? null,
    systemConfig: systemConfig?.config ?? null,
  });
}

/**
 * The deployment tenant's branding, fetched once after login and held in
 * the store. Separate from the masters so a branding failure never blocks
 * them. On failure branding stays null and the UI shows no airline -- never
 * a hardcoded one.
 */
export async function hydrateBranding(): Promise<void> {
  try {
    useAppStore.setState({ branding: await fetchBranding() });
  } catch (err) {
    console.error('[hydrateBranding] Failed to load branding:', err);
    useAppStore.setState({ branding: null });
  }
}

/** Re-fetch tenant settings after Settings → Airline settings changes them. */
export async function refreshTenantSettings(): Promise<void> {
  const systemConfig = await fetchSystemConfig();
  useAppStore.setState({
    tenantSettings: systemConfig?.settings ?? null,
    systemConfig: systemConfig?.config ?? null,
  });
}

/** Re-fetch the rank master after Settings → Ranks changes it. */
export async function refreshRanks(): Promise<void> {
  useAppStore.setState({ ranks: await fetchRanks() });
}
